import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Banknote, Save, ShieldAlert } from 'lucide-react'
import { env } from '@/lib/env'
import { formatDateTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { InstapayConfig } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/StatusBadge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input, Textarea } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { fetchInstapayConfig, updateInstapayConfig } from './api'
import styles from './InstapayConfigPage.module.css'

interface FormState {
  bankName: string
  accountNumber: string
  accountHolderName: string
  instructions: string
  active: boolean
}

const EMPTY: FormState = {
  bankName: '',
  accountNumber: '',
  accountHolderName: '',
  instructions: '',
  active: false,
}

/**
 * The bank account customers are told to transfer to when paying by Instapay.
 *
 * This exists as a screen rather than a config file because a wrong account
 * number sends customers' money to a stranger, and correcting it must not wait
 * on an App Store review. The mobile app reads these four fields from the API
 * on every Instapay booking.
 */
export function InstapayConfigPage() {
  const [form, setForm] = useState<FormState>(EMPTY)

  useEffect(() => {
    document.title = `Instapay account · ${env.appName} Operations`
  }, [])

  const configQuery = useQuery({
    queryKey: queryKeys.payments.instapayConfig,
    queryFn: fetchInstapayConfig,
  })

  // Seeded once from the server, then owned by the form — refetching must not
  // discard an operator's half-typed account number.
  useEffect(() => {
    const config = configQuery.data
    if (!config) return
    setForm({
      bankName: config.bankName ?? '',
      accountNumber: config.accountNumber ?? '',
      accountHolderName: config.accountHolderName ?? '',
      instructions: config.instructions ?? '',
      active: config.active,
    })
  }, [configQuery.data])

  const mutation = useAdminMutation({
    mutationFn: () =>
      updateInstapayConfig({
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        accountHolderName: form.accountHolderName.trim(),
        instructions: form.instructions.trim() || null,
        active: form.active,
      }),
    successTitle: 'Instapay account saved',
    successDescription: (saved: InstapayConfig) =>
      saved.active
        ? 'Customers paying by Instapay will now be shown these details.'
        : 'Saved, but Instapay is switched off — customers will not be offered it.',
    errorTitle: 'The Instapay account was not saved',
    invalidate: [queryKeys.payments.all],
  })

  const complete =
    form.bankName.trim().length > 0 &&
    form.accountNumber.trim().length > 0 &&
    form.accountHolderName.trim().length > 0

  const canSubmit = complete && !mutation.isPending

  return (
    <>
      <PageHeader
        title="Instapay account"
        description="The bank details shown to customers paying by Instapay. Changes take effect on the next booking — the app reads them from here rather than shipping them in a release."
        meta={
          configQuery.data ? (
            <>
              <Badge tone={configQuery.data.usable && configQuery.data.active ? 'success' : 'warning'} dot>
                {configQuery.data.active
                  ? configQuery.data.usable
                    ? 'Offered to customers'
                    : 'Active but incomplete'
                  : 'Not offered'}
              </Badge>
              {configQuery.data.updatedAt ? (
                <span>
                  Last changed {formatDateTime(configQuery.data.updatedAt)}
                  {configQuery.data.updatedByAdminName ? ` by ${configQuery.data.updatedByAdminName}` : ''}
                </span>
              ) : null}
            </>
          ) : null
        }
      />

      <Card>
        {configQuery.isPending ? (
          <div className={styles.loading}>
            <Spinner /> <span>Loading the Instapay account…</span>
          </div>
        ) : configQuery.isError ? (
          <p className={styles.error} role="alert">
            The Instapay account could not be loaded.{' '}
            <Button variant="ghost" size="sm" onClick={() => void configQuery.refetch()}>
              Try again
            </Button>
          </p>
        ) : (
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault()
              if (canSubmit) mutation.mutate(undefined)
            }}
          >
            <Field label="Bank name" required hint="As the customer will see it, e.g. Banque Misr.">
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  value={form.bankName}
                  maxLength={150}
                  onChange={(event) => setForm({ ...form, bankName: event.target.value })}
                  disabled={mutation.isPending}
                />
              )}
            </Field>

            <Field
              label="Account number or IPA"
              required
              hint="Check this character by character. A wrong value sends customers' money to a stranger."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  value={form.accountNumber}
                  maxLength={100}
                  onChange={(event) => setForm({ ...form, accountNumber: event.target.value })}
                  disabled={mutation.isPending}
                />
              )}
            </Field>

            <Field label="Account holder name" required hint="The company name as it appears on the transfer.">
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  value={form.accountHolderName}
                  maxLength={150}
                  onChange={(event) => setForm({ ...form, accountHolderName: event.target.value })}
                  disabled={mutation.isPending}
                />
              )}
            </Field>

            <Field
              label="Instructions (optional)"
              hint="Shown under the account details in the app."
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  value={form.instructions}
                  rows={3}
                  maxLength={1000}
                  placeholder="Include your booking reference in the transfer note."
                  onChange={(event) => setForm({ ...form, instructions: event.target.value })}
                  disabled={mutation.isPending}
                />
              )}
            </Field>

            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => setForm({ ...form, active: event.target.checked })}
                disabled={mutation.isPending}
              />
              <span>
                <strong>Offer Instapay to customers</strong>
                <span className={styles.toggleHint}>
                  When off, the app does not show Instapay as a payment method at all.
                </span>
              </span>
            </label>

            {form.active && !complete ? (
              <p className={styles.warning} role="alert">
                <ShieldAlert size={16} aria-hidden="true" />
                Fill in the bank, account number and account holder before switching Instapay on. The
                server will refuse an active account with a blank field.
              </p>
            ) : null}

            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                iconLeft={<Save aria-hidden="true" />}
                loading={mutation.isPending}
                disabled={!canSubmit}
              >
                {mutation.isPending ? 'Saving…' : 'Save account'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      <Card>
        <div className={styles.preview}>
          <h2 className={styles.previewTitle}>
            <Banknote size={16} aria-hidden="true" /> What the customer sees
          </h2>
          <dl className={styles.previewGrid}>
            <div>
              <dt>Bank</dt>
              <dd>{form.bankName || '—'}</dd>
            </div>
            <div>
              <dt>Account number</dt>
              <dd className={styles.mono}>{form.accountNumber || '—'}</dd>
            </div>
            <div>
              <dt>Account holder</dt>
              <dd>{form.accountHolderName || '—'}</dd>
            </div>
          </dl>
          {form.instructions ? <p className={styles.previewNote}>{form.instructions}</p> : null}
          <p className={styles.previewAmount}>
            The amount to transfer is calculated per booking by the server, after any points the
            customer has. It is never entered here.
          </p>
        </div>
      </Card>
    </>
  )
}
